import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  NativeSelect,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useWorkflows,
  type ListWorkflowsFilters,
  type WorkflowDefinitionGraph,
  type WorkflowDefinitionStatus,
} from '../api/workflows';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';

const STATUS_COLOURS: Record<WorkflowDefinitionStatus, string> = {
  draft: 'yellow',
  active: 'green',
  archived: 'gray',
};

function statusBadge(status: WorkflowDefinitionStatus) {
  return (
    <Badge color={STATUS_COLOURS[status] ?? 'gray'} variant="light">
      {status}
    </Badge>
  );
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/** Count keys in a definition's `steps` map, defensive against malformed JSON. */
function stepCount(definition: unknown): number {
  const graph = definition as Partial<WorkflowDefinitionGraph> | null;
  if (!graph || typeof graph !== 'object' || !graph.steps) return 0;
  return Object.keys(graph.steps).length;
}

export function WorkflowsListPage() {
  // Admin operators are cross-tenant by design, so the list returns
  // workflows across all orgs by default. The org + product selectors
  // let the operator narrow the view without typing UUIDs.
  const orgs = useOrgs();
  const products = useProducts();

  const [orgId, setOrgId] = useState('');
  const [productId, setProductId] = useState('');
  const [status, setStatus] = useState<'' | WorkflowDefinitionStatus>('');

  const filters: ListWorkflowsFilters = {
    orgId: orgId || undefined,
    productId: productId || undefined,
    status: status || undefined,
  };
  const query = useWorkflows(filters);
  const orgNameById = new Map((orgs.data ?? []).map((o) => [o.id, o.name]));
  // Same id → label lookup as the Organisation column. Workflows whose
  // `productId` is missing from the loaded products list (e.g. the
  // product was soft-deleted) fall back to the raw UUID so operators
  // can still grep / cross-reference.
  const productNameById = new Map((products.data ?? []).map((p) => [p.id, p.displayName]));

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={2}>Workflows</Title>
        <Group gap="sm">
          <Button component={Link} to="/reason-codes" variant="default">
            Reason Codes
          </Button>
          <Button component={Link} to="/workflows/new" leftSection={<IconPlus size={16} />}>
            New workflow
          </Button>
        </Group>
      </Group>

      <Group gap="md" align="flex-end" wrap="wrap">
        <NativeSelect
          label="Organisation"
          value={orgId}
          onChange={(e) => setOrgId(e.currentTarget.value)}
          data={[
            { value: '', label: 'All organisations' },
            ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
          ]}
          w={240}
        />
        <NativeSelect
          label="Product"
          value={productId}
          onChange={(e) => setProductId(e.currentTarget.value)}
          data={[
            { value: '', label: 'All products' },
            ...(products.data ?? []).map((p) => ({ value: p.id, label: p.displayName })),
          ]}
          w={240}
        />
        <NativeSelect
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.currentTarget.value as WorkflowDefinitionStatus | '')}
          data={[
            { value: '', label: 'Any' },
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' },
          ]}
          w={200}
        />
      </Group>

      {query.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Could not load workflows.
        </Alert>
      )}

      {query.isPending ? (
        <Center mih={200}>
          <Text c="dimmed">Loading…</Text>
        </Center>
      ) : (query.data?.items ?? []).length === 0 ? (
        <Center mih={200}>
          <Text c="dimmed">No workflows match these filters.</Text>
        </Center>
      ) : (
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Organisation</Table.Th>
              <Table.Th>Product</Table.Th>
              <Table.Th ta="right">Version</Table.Th>
              <Table.Th ta="right">Steps</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Mode</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Published</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(query.data?.items ?? []).map((w) => (
              <Table.Tr key={w.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/workflows/${w.id}`} size="sm">
                    {w.name}
                  </Anchor>
                  {w.description && (
                    <Text size="xs" c="dimmed">
                      {w.description}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {w.orgId === null ? (
                    <Text size="sm" c="dimmed" fs="italic">
                      {w.isTemplate ? 'Template' : 'Default — all orgs'}
                    </Text>
                  ) : (
                    <Text size="sm">{orgNameById.get(w.orgId) ?? w.orgId}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {w.productId === null ? (
                    <Text size="sm" c="dimmed" fs="italic">
                      —
                    </Text>
                  ) : (
                    <Text size="sm">{productNameById.get(w.productId) ?? w.productId}</Text>
                  )}
                </Table.Td>
                <Table.Td ta="right">v{w.version}</Table.Td>
                <Table.Td ta="right">{stepCount(w.definition)}</Table.Td>
                <Table.Td>{statusBadge(w.status)}</Table.Td>
                <Table.Td>
                  <Text size="sm">{w.defaultMode}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" title={w.createdAt}>
                    {formatDate(w.createdAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" title={w.publishedAt ?? ''}>
                    {formatDate(w.publishedAt)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
